import { useMemo, useState } from 'react';
import { CLAIM_SUBMISSION_ROUTES } from '@fixtyer/content';
import { GuideLayout, GuideSection, GuideTip } from '@/components/guides/guide-layout';
import { PhaseFiveGuideNotes } from '@/components/guides/phase-five-guide-notes';

const routeKindLabel = {
  'online-form': 'Online form',
  'downloadable-form': 'Downloadable form',
  'contact-route': 'Contact route',
  'guidance-page': 'Official guidance',
} as const;

function formatVerificationDate(value: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));
}

export function HighwayAuthorityClaimRoutesGuide() {
  const [query, setQuery] = useState('');
  const filteredRoutes = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('en-GB');
    if (!normalized) return CLAIM_SUBMISSION_ROUTES;
    return CLAIM_SUBMISSION_ROUTES.filter((route) =>
      [
        route.authorityName,
        route.areaLabel,
        route.nation,
        route.roadScope,
      ].some((value) => value.toLocaleLowerCase('en-GB').includes(normalized))
    );
  }, [query]);

  return (
    <GuideLayout
      slug="highway-authority-claim-routes"
      category="Directory"
      categoryColor="brand"
      title="Highway Authority Claim Routes Directory"
      seoTitle="Highway Authority Claim Routes Directory | Fixtyer"
      description="Search a curated directory of verified official pothole and highway-damage claim routes for selected England and Wales authorities."
      readTime="6 min"
      updatedDate="2026-09-07"
      reviewedDate="2026-07-19"
      datePublished="2026-07-19"
      reviewedFor="England and Wales"
      contentOwner="Elevyn Technology Group Limited editorial team"
      sources={[
        {
          name: 'GOV.UK vehicle-damage claims',
          url: 'https://www.gov.uk/claim-for-damage-to-your-vehicle',
        },
        {
          name: 'Traffic Wales strategic-road claims guidance',
          url: 'https://traffic.wales/claims',
        },
      ]}
      atAGlance={[
        {
          label: 'Answer first',
          value:
            'Use the verified route for the authority that maintained the road, not merely the authority named in the postal address.',
        },
        {
          label: 'Coverage',
          value:
            'Curated partial coverage: national strategic-road routes, TfL and representative English and Welsh local authorities.',
        },
        {
          label: 'Verification',
          value:
            'Every entry links to an authority-controlled route and records the primary source and verification date.',
        },
      ]}
      relatedGuides={[
        {
          slug: 'who-is-responsible',
          title: 'Who Is Responsible?',
          readTime: '4 min',
        },
        {
          slug: 'england-wales-pothole-claims',
          title: 'England vs Wales',
          readTime: '7 min',
        },
        {
          slug: 'national-highways-pothole-claims',
          title: 'National Highways Claims',
          readTime: '7 min',
        },
      ]}
    >
      <GuideTip variant="info" title="Honest coverage note">
        Coverage is intentionally partial and is not a list of every highway authority. If your
        authority is absent, start with GOV.UK&apos;s council finder and verify the current claim
        route on the authority&apos;s own website.
      </GuideTip>

      <GuideSection title="Search verified routes" icon={<span aria-hidden="true">⌕</span>}>
        <label htmlFor="claim-route-search" className="mb-2 block font-semibold text-brand-950">
          Authority, area or nation
        </label>
        <input
          id="claim-route-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="For example: Suffolk, London or Wales"
          className="mb-6 w-full rounded-xl border border-surface-300 px-4 py-3"
        />

        <p aria-live="polite" className="mb-4 text-sm text-surface-600">
          Showing {filteredRoutes.length} verified route{filteredRoutes.length === 1 ? '' : 's'}
        </p>
        <div className="space-y-5">
          {filteredRoutes.map((route) => (
            <article
              key={route.authorityName}
              className="rounded-xl border border-surface-200 bg-surface-50 p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-semibold text-brand-950">
                    {route.authorityName}
                  </h3>
                  <p className="text-sm text-surface-600">
                    {route.areaLabel} · {route.nation} · {routeKindLabel[route.routeKind]}
                  </p>
                </div>
                <span className="rounded-full bg-success-500/10 px-3 py-1 text-xs font-semibold text-success-700">
                  Verified {formatVerificationDate(route.verifiedDate)}
                </span>
              </div>
              <p className="mt-3">
                <strong>Road scope:</strong> {route.roadScope}
              </p>
              <p className="mt-2">{route.scopeNote}</p>
              <p className="mt-2">{route.submissionNote}</p>
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                <a
                  href={route.officialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-brand-600 underline"
                >
                  Open official route
                </a>
                <a
                  href={route.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-surface-700 underline"
                >
                  Verification source
                </a>
              </div>
            </article>
          ))}
        </div>
        {filteredRoutes.length === 0 && (
          <p className="rounded-xl border border-surface-200 p-5">
            No curated match. Use GOV.UK to identify the maintaining authority, then verify its
            current route on its official website.
          </p>
        )}
      </GuideSection>

      <PhaseFiveGuideNotes />
    </GuideLayout>
  );
}
